/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useCallback, useMemo, type ReactNode } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useNotifications } from '@jira/polaris__lib-notifications/src/useNotifications.tsx';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import Lozenge from '@atlaskit/lozenge/lozenge';
import Tag from '@atlaskit/tag/removable-tag';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { getEntityLimitError, getErrorMessageDescriptor } from './EntityLimitError.tsx';
import { PolarisAccessLossError } from './PolarisAccessLossError.tsx';
import type { ErrorHandlers, ExperienceCallback } from './IssueError.types.tsx';
import { getMetadata, isConfigError, isPermissionError } from './isPermissionError.tsx';

const errorTitle = defineMessage({
	id: 'polaris-lib-errors.controllers.error-title',
	defaultMessage: 'Something went wrong.',
	description: 'Flag title to communicate an error.',
});

const permissionErrorDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.permission-error-description',
	defaultMessage: 'You may not have the permissions required to perform this operation.',
	description: 'Flag title to communicate an error.',
});

const configErrorTitle = defineMessage({
	id: 'polaris-lib-errors.controllers.config-error-title',
	defaultMessage: "We couldn't save that.",
	description:
		'The title shown when a configuration update fails due to data limits on the server.',
});

const configErrorDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.config-error-description',
	defaultMessage:
		'This usually happens because the configuration data on this field is too big. Contact support for more details',
	description:
		'The description shown when a configuration update fails due to data limits on the server.',
});

const serverLoadFailedDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.server-load-failed-description',
	defaultMessage:
		'We had difficulties to fetch some data from the server. Please refresh the page.',
	description: 'Flag description for an error occurring while loading data from the server',
});

const serverUpdateFailedDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.server-update-failed-description',
	defaultMessage:
		'We had some difficulties to save some data to the server. Please try again or refresh the page.',
	description: 'Flag description for an error occurring while updating data on the server',
});

const errorDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.error-description',
	defaultMessage:
		'We failed to execute your last operation. Our team has been notified. Please refresh the page to try again.',
	description: 'Flag description for a client code error.',
});

const errorPossibleAccessLoss = defineMessage({
	id: 'polaris-lib-errors.controllers.error-possible-access-loss',
	defaultMessage: 'You may have lost access to the project or site. Please refresh the page.',
	description:
		'Flag description for situation where the user may have lost access to the site or the project',
});

const cyclicFormulaFieldDescription = defineMessage({
	id: 'polaris-lib-errors.controllers.cyclic-formula-field-description',
	defaultMessage:
		'We cannot calculate the value of some fields because they contain a cyclic formula. Please edit the field configurations and refresh: {fieldLabels}',
	description: 'Description to show to the user if a cyclic formula field is detected',
});

const entityLimitErrorTitle = defineMessage({
	id: 'polaris-lib-errors.controllers.entity-limit-error-title',
	defaultMessage: 'You’ve hit a limit',
	description: 'Flag title to communicate an entity limit error.',
});

const errorPossibleAccessLossGalaxia = defineMessage({
	id: 'polaris-lib-errors.controllers.error-possible-access-loss-galaxia',
	defaultMessage: 'You may have lost access to the space or site. Please refresh the page.',
	description:
		'Flag description for situation where the user may have lost access to the site or the project',
});

type CreateErrorAnalyticsOptions = {
	sendToPrivacyUnsafeSplunk?: boolean;
	skipSentry?: boolean;
};

export const createErrorAnalytics = (
	id: string,
	error: Error,
	options?: CreateErrorAnalyticsOptions,
): {
	meta: {
		id: string;
	};
	error: Error;
	sendToPrivacyUnsafeSplunk: boolean;
	skipSentry: boolean;
} => ({
	meta: { id },
	error,
	sendToPrivacyUnsafeSplunk: options?.sendToPrivacyUnsafeSplunk ?? true,
	skipSentry: options?.skipSentry ?? false,
});

const handleCallback = (errorObject: Error, callback?: ExperienceCallback) => {
	callback && callback(getMetadata(errorObject));
};

const useGeneralDataLoadingFailedHandling = () => {
	const { formatMessage } = useIntl();
	const { errorWithRefresh } = useNotifications();
	return useCallback(
		(errorObject: Error, callback?: ExperienceCallback) => {
			handleCallback(errorObject, callback);
			errorWithRefresh({
				title: formatMessage(errorTitle),
				description: formatMessage(serverLoadFailedDescription),
			});
			fireErrorAnalytics(
				createErrorAnalytics('polaris.error.generalDataLoadingFailed', errorObject),
			);
		},
		[errorWithRefresh, formatMessage],
	);
};

const useGeneralDataUpdateFailedHandling = () => {
	const { formatMessage } = useIntl();
	const { error, errorWithRefresh, customFlag } = useNotifications();
	return useCallback(
		(errorObject: Error, callback?: ExperienceCallback) => {
			handleCallback(errorObject, callback);

			fireErrorAnalytics(
				createErrorAnalytics('polaris.error.generalDataUpdateFailed', errorObject),
			);

			const entityLimitError = getEntityLimitError(errorObject);
			if (entityLimitError) {
				const errorMessage = getErrorMessageDescriptor(entityLimitError);
				return errorMessage
					? customFlag({
							title: formatMessage(entityLimitErrorTitle),
							description: formatMessage(errorMessage, {
								item: (chunks: ReactNode[]) =>
									// we are using feature gate here, because tag's text type is different then lozenge's children type. we need to convert chunks to string when feature gate is on.
									fg('platform-dst-lozenge-tag-badge-visual-uplifts') ? (
										<Tag
											text={chunks.map(String).join('')}
											color="standard"
											isRemovable={false}
											hasMargin={false}
											migration_fallback="lozenge"
										/>
									) : (
										<Lozenge>{chunks}</Lozenge>
									),
							}),
							icon: (
								<ErrorIcon spacing="spacious" label="Error" color={token('color.icon.danger')} />
							),
						})
					: null;
			}

			if (isPermissionError(errorObject)) {
				return error({
					title: formatMessage(errorTitle),
					description: formatMessage(permissionErrorDescription),
				});
			}

			if (isConfigError(errorObject)) {
				return error({
					title: formatMessage(configErrorTitle),
					description: formatMessage(configErrorDescription),
				});
			}

			errorWithRefresh({
				title: formatMessage(errorTitle),
				description: formatMessage(serverUpdateFailedDescription),
			});
		},
		[customFlag, error, errorWithRefresh, formatMessage],
	);
};

const useGeneralActionFailedHandling = () => {
	const { formatMessage } = useIntl();
	const { errorWithRefresh } = useNotifications();
	return useCallback(
		(errorObject: Error, callback?: ExperienceCallback) => {
			handleCallback(errorObject, callback);
			errorWithRefresh({
				title: formatMessage(errorTitle),
				description: formatMessage(errorDescription),
			});
			fireErrorAnalytics(createErrorAnalytics('polaris.error.generalActionFailed', errorObject));
		},
		[errorWithRefresh, formatMessage],
	);
};

const usePossibleAccessLossError = () => {
	const { formatMessage } = useIntl();
	const { errorWithRefresh } = useNotifications();
	return useCallback(
		(errorObject: Error, callback?: ExperienceCallback) => {
			handleCallback(errorObject, callback);
			errorWithRefresh({
				title: formatMessage(errorTitle),
				description: formatMessage(
					expVal('project-terminology-refresh', 'isEnabled', false)
						? errorPossibleAccessLossGalaxia
						: errorPossibleAccessLoss,
				),
			});
			// Skip Sentry for PolarisAccessLossError since it's an expected business scenario
			// (e.g. 403 when user loses access to a view).
			const skipSentry = errorObject instanceof PolarisAccessLossError;
			fireErrorAnalytics(
				createErrorAnalytics('polaris.error.generalActionFailed', errorObject, { skipSentry }),
			);
		},
		[errorWithRefresh, formatMessage],
	);
};

export const useErrorHandlers = (): ErrorHandlers => {
	const possibleAccessLossError = usePossibleAccessLossError();
	const generalActionFailedError = useGeneralActionFailedHandling();
	const generalDataUpdateFailedError = useGeneralDataUpdateFailedHandling();
	const generalDataLoadingFailedError = useGeneralDataLoadingFailedHandling();

	return useMemo(
		() => ({
			possibleAccessLossError,
			generalActionFailedError,
			generalDataUpdateFailedError,
			generalDataLoadingFailedError,
		}),
		[
			possibleAccessLossError,
			generalActionFailedError,
			generalDataUpdateFailedError,
			generalDataLoadingFailedError,
		],
	);
};

export const useOnCyclicFormulaFieldError = (): ((fieldLabels: string[]) => void) => {
	const { formatMessage } = useIntl();
	const { error } = useNotifications();
	return useCallback(
		(fieldLabels: string[]) => {
			error({
				title: formatMessage(errorTitle),
				description: formatMessage(cyclicFormulaFieldDescription, {
					fieldLabels: fieldLabels.join(', '),
				}),
			});
			fireErrorAnalytics(
				createErrorAnalytics('polaris.error.cyclicFormula', new Error('Cyclic formula field')),
			);
		},
		[error, formatMessage],
	);
};
